import { useQuery } from '@tanstack/react-query';
import { isPlanChangeNotEnabled } from '../lib/planChangeErrors';
import { listPlanChanges } from '../services/planChange.service';

export const PLAN_CHANGE_HISTORY_QUERY_KEY = (subscriptionId?: number) =>
	['plan-change', 'history', subscriptionId] as const;

/**
 * Whether this subscription can change plans from the web. The engine is the
 * only gate (PLAN_CHANGE_ENABLED / PLAN_CHANGE_USERS): its history route answers
 * 503 to anyone it is not open to, so the web never keeps a second list of who
 * may. While unknown or refused, surfaces keep what they showed before.
 *
 * The 503 is an answer, not an error: it is cached as `null` for the
 * `staleTime` like any other answer, so a user it is not open to asks once per
 * subscription, not on every mount or tab focus (review #856).
 */
export const usePlanChangeAvailability = (subscriptionId?: number) => {
	const query = useQuery({
		queryKey: PLAN_CHANGE_HISTORY_QUERY_KEY(subscriptionId),
		queryFn: () =>
			listPlanChanges(subscriptionId as number).catch((error: unknown) => {
				if (isPlanChangeNotEnabled(error)) return null;
				throw error;
			}),
		enabled: subscriptionId != null,
		retry: false,
		refetchOnWindowFocus: false,
		staleTime: 5 * 60 * 1000,
	});
	return {
		available: query.data != null,
		changes: query.data ?? [],
		refetch: query.refetch,
	};
};
